import { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { AuthLayout } from '@/components/layout/AuthLayout'
import { Input } from '@/components/ui/Input'
import { Notice } from '@/components/ui/Notice'

interface Props {
  email?: string
  unverified?: boolean
  resent?: boolean
}

export default function VerifyEmail({ email = '', unverified = false, resent = false }: Props) {
  const [currentEmail, setCurrentEmail] = useState(email)
  const [processing, setProcessing] = useState(false)
  const [sent, setSent] = useState(resent)

  function handleResend(e: React.FormEvent) {
    e.preventDefault()
    setProcessing(true)
    router.post(
      '/users/confirmation',
      { user: { email: currentEmail } },
      {
        onSuccess: () => setSent(true),
        onFinish: () => setProcessing(false),
      },
    )
  }

  return (
    <AuthLayout>
      <div className="w-full max-w-sm">

        {/* Icon — decorative */}
        <div className="flex justify-center mb-8" aria-hidden="true">
          <div className="w-12 h-12 rounded-full border border-border-divider bg-surface-bg flex items-center justify-center">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-accent" aria-hidden="true" focusable="false">
              <rect x="2" y="4" width="20" height="16" rx="2" />
              <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
            </svg>
          </div>
        </div>

        {/* Heading */}
        <div className="text-center mb-8">
          <h1 className="font-serif text-3xl font-medium text-heading mb-2">
            {unverified ? 'Verify your email' : 'Check your inbox'}
          </h1>
          <p className="text-sm text-muted font-light leading-relaxed">
            {unverified
              ? 'Your account isn\'t verified yet. Click the link we sent to confirm your email before signing in.'
              : `We sent a confirmation link to ${email || 'your email address'}. Click it to activate your account.`
            }
          </p>
        </div>

        {/* Success state */}
        {sent ? (
          <Notice className="mb-6">A new confirmation link has been sent.</Notice>
        ) : null}

        {/* Resend form */}
        <form onSubmit={handleResend} className="flex flex-col gap-5">
          <Input
            id="email"
            label="Email address"
            type="email"
            autoComplete="email"
            value={currentEmail}
            onChange={e => setCurrentEmail(e.target.value)}
            required
          />

          <button
            type="submit"
            disabled={processing}
            aria-busy={processing}
            className="w-full py-3 bg-btn-filled-bg text-btn-filled-text text-sm font-light tracking-wide rounded-full hover:bg-btn-filled-hover-bg transition-colors duration-300 disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
          >
            {processing ? 'Sending…' : sent ? 'Resend again' : 'Resend confirmation email'}
          </button>
        </form>

        <p className="mt-8 text-center text-xs text-muted font-light">
          <Link
            href="/users/sign_in"
            className="text-body hover:text-heading transition-colors duration-200 underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 rounded"
          >
            Back to sign in
          </Link>
        </p>

      </div>
    </AuthLayout>
  )
}
